import * as yup from 'yup';
import { UseFormProps } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { FormDataType } from './types';
import { Currencies } from '../../enums';
import { reDate, reEmail, rePhone } from '@shared/lib/regex';
import { isValidDate } from '@modules/employees/shared/helpers';
import {
  FUTURE_OPEN_ACCOUNT_DATE,
  FUTURE_REGISTRATION_DATE,
  INVALID_DATE_FORMAT,
  INVALID_EMAIL,
  INVALID_PHONE_NUMBER,
  MIN_AGE_CONSTRAINT,
  REQUIRED_EMAIL,
  REQUIRED_FIRST_NAME,
  REQUIRED_LAST_NAME,
} from '@modules/employees/shared/constants/form-error.constants';

const schema = yup
  .object()
  .shape({
    firstName: yup.string().required(REQUIRED_FIRST_NAME),
    lastName: yup.string().required(REQUIRED_LAST_NAME),
    fullNameUa: yup.string().default(''),
    birthDate: yup
      .string()
      .matches(reDate, {
        excludeEmptyString: true,
        message: INVALID_DATE_FORMAT,
      })
      .test('dateOfBirth', MIN_AGE_CONSTRAINT, value =>
        isValidDate(value, {
          isYearValidation: true,
        })
      )
      .default(''),

    corporateMail: yup.string().required(REQUIRED_EMAIL).matches(reEmail, INVALID_EMAIL),
    phoneNumber: yup.object().shape({
      code: yup.string().default(''),
      phone: yup
        .string()
        .matches(rePhone, {
          excludeEmptyString: true,
          message: INVALID_PHONE_NUMBER,
        })
        .default(''),
    }),

    companyName: yup.string().default(''),
    registrationNumber: yup.string().default(''),
    registrationDate: yup
      .string()
      .matches(reDate, {
        excludeEmptyString: true,
        message: INVALID_DATE_FORMAT,
      })
      .test('registrationDate', FUTURE_REGISTRATION_DATE, value =>
        isValidDate(value, {
          extraDays: 14,
        })
      )
      .default(''),
    registrationCountry: yup.string().default(''),
    taxIdentificationNumber: yup.string().default(''),
    addressEn: yup.string().default(''),
    addressUa: yup.string().default(''),

    country: yup.string().default(''),
    bankAccount: yup.string().default(''),
    currency: yup.string<Currencies>().default(Currencies.EUR),
    bankNameUa: yup.string().default(''),
    bankNameEn: yup.string().default(''),
    bankAddressUa: yup.string().default(''),
    bankAddressEn: yup.string().default(''),
    bankCode: yup.string().default(''),
    openAccountDate: yup
      .string()
      .matches(reDate, {
        excludeEmptyString: true,
        message: INVALID_DATE_FORMAT,
      })
      .test('bankDate', FUTURE_OPEN_ACCOUNT_DATE, value => isValidDate(value))
      .default(''),
  })
  .required();

const defaultValues: FormDataType = {
  currency: Currencies.EUR,
  firstName: '',
  lastName: '',
  fullNameUa: '',
  birthDate: '',
  corporateMail: '',
  phoneNumber: {
    code: '',
    phone: '',
  },
  companyName: '',
  registrationNumber: '',
  registrationDate: '',
  taxIdentificationNumber: '',
  addressEn: '',
  addressUa: '',
  bankAccount: '',
  bankNameUa: '',
  bankNameEn: '',
  bankAddressUa: '',
  bankAddressEn: '',
  bankCode: '',
  openAccountDate: '',
  registrationCountry: '',
  country: '',
};

export const options: UseFormProps<FormDataType> = {
  defaultValues,
  resolver: yupResolver<FormDataType>(schema),
};
